import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faPen, faPlus, faTrash, faXmark } from '@fortawesome/free-solid-svg-icons';
import { unitsApi, type UnitDef, type UnitKind } from '../lib/domain';
import { ApiError } from '../lib/api';
import { KIND_LABEL, useUnits } from '../lib/units';

interface Draft {
  label: string;
  kind: UnitKind;
  factor: string;
  allowDecimal: boolean;
}

const EMPTY: Draft = { label: '', kind: 'COUNT', factor: '1', allowDecimal: false };

function needsFactor(kind: UnitKind): boolean {
  return kind === 'WEIGHT' || kind === 'VOLUME';
}

function factorHint(kind: UnitKind): string {
  return kind === 'WEIGHT' ? 'Faktor in kg (g = 0,001)' : 'Faktor in l (ml = 0,001)';
}

// Einheiten je Gruppe pflegen (Admins); Mitglieder sehen die Liste nur.
export function UnitsEditor({ groupId, canEdit }: { groupId: string; canEdit: boolean }) {
  const units = useUnits(`group:${groupId}`);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [editing, setEditing] = useState<{ unit: UnitDef; draft: Draft } | null>(null);
  const [error, setError] = useState<string | null>(null);

  function toBody(d: Draft) {
    const factor = Number(d.factor.replace(',', '.'));
    return { label: d.label.trim(), kind: d.kind, factor: needsFactor(d.kind) && factor > 0 ? factor : undefined, allowDecimal: d.allowDecimal };
  }

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await units.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Aktion fehlgeschlagen. Bitte erneut versuchen.');
    }
  }

  function editor(d: Draft, set: (d: Draft) => void) {
    return (
      <div className="row">
        <input value={d.label} onChange={(e) => set({ ...d, label: e.target.value })} placeholder="Bezeichnung, z. B. Bund" aria-label="Bezeichnung" />
        <select value={d.kind} onChange={(e) => set({ ...d, kind: e.target.value as UnitKind })} aria-label="Art">
          {(Object.keys(KIND_LABEL) as UnitKind[]).map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </select>
        {needsFactor(d.kind) && (
          <input value={d.factor} onChange={(e) => set({ ...d, factor: e.target.value })} style={{ width: 90 }} aria-label={factorHint(d.kind)} title={factorHint(d.kind)} />
        )}
        <label className="row">
          <input type="checkbox" checked={d.allowDecimal} onChange={(e) => set({ ...d, allowDecimal: e.target.checked })} />
          Dezimal
        </label>
      </div>
    );
  }

  return (
    <div className="card stack">
      <h2>Einheiten</h2>
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <ul className="list-reset stack">
        {units.units.map((u) => (
          <li key={u.code}>
            {editing?.unit.code === u.code ? (
              <div className="stack">
                {editor(editing.draft, (d) => setEditing({ unit: u, draft: d }))}
                <div className="row">
                  <button
                    className="primary"
                    aria-label="Speichern"
                    title="Speichern"
                    disabled={!editing.draft.label.trim()}
                    onClick={() => run(async () => {
                      await unitsApi.update(u.id!, toBody(editing.draft));
                      setEditing(null);
                    })}
                  >
                    <FontAwesomeIcon icon={faCheck} />
                  </button>
                  <button aria-label="Abbrechen" title="Abbrechen" onClick={() => setEditing(null)}>
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="row between">
                <span>
                  {u.label} <span className="muted">({KIND_LABEL[u.kind]}{u.allowDecimal ? ', dezimal' : ''})</span>
                </span>
                {canEdit && u.id && (
                  <span className="row">
                    <button
                      aria-label="Einheit bearbeiten"
                      title="Einheit bearbeiten"
                      onClick={() => setEditing({ unit: u, draft: { label: u.label, kind: u.kind, factor: String(u.factor), allowDecimal: u.allowDecimal } })}
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button
                      className="danger"
                      aria-label="Einheit löschen"
                      title="Einheit löschen"
                      onClick={() => confirm(`Einheit „${u.label}“ wirklich löschen?`) && run(() => unitsApi.remove(u.id!))}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </span>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      {canEdit && (
        <div className="stack">
          {editor(draft, setDraft)}
          <div>
            <button
              className="primary"
              aria-label="Einheit hinzufügen"
              title="Einheit hinzufügen"
              disabled={!draft.label.trim()}
              onClick={() => run(async () => {
                await unitsApi.create({ ...toBody(draft), ownerGroupId: groupId });
                setDraft(EMPTY);
              })}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
